/**
 * Axios Interceptor Configuration
 * Sets up global error handling for API requests
 */

import axios from 'axios';
import toast from 'react-hot-toast';
import errorTracker from './errorTracking';

/**
 * Configure Axios interceptors for error tracking
 */
export const setupAxiosInterceptors = () => {
  // Request interceptor - add request context
  axios.interceptors.request.use(
    (config) => {
      // Add timestamp for performance tracking
      config.metadata = { startTime: new Date() };
      return config;
    },
    (error) => {
      errorTracker.captureAPIError(error, { phase: 'request' });
      return Promise.reject(error);
    }
  );

  // Response interceptor - handle errors
  axios.interceptors.response.use(
    (response) => {
      // Calculate request duration
      if (response.config.metadata) {
        const duration = new Date() - response.config.metadata.startTime;
        
        // Log slow requests (over 3 seconds)
        if (duration > 3000) {
          errorTracker.log(
            `Slow API request detected: ${response.config.url}`,
            'warning',
            {
              url: response.config.url,
              method: response.config.method,
              duration,
            }
          );
        }
      }
      
      return response;
    },
    (error) => {
      const responseDetail = error.response?.data?.detail;
      const demoRestrictionHeader = error.response?.headers?.['x-demo-restriction'];
      const isDemoRestriction =
        error.response?.status === 403 &&
        (
          demoRestrictionHeader === 'true' ||
          (typeof responseDetail === 'string' && (
            responseDetail.toLowerCase().includes('demo mode') ||
            responseDetail.toLowerCase().includes('real account') ||
            responseDetail.toLowerCase().includes('demo account')
          ))
        );

      // Capture API errors with full context
      const errorContext = {
        phase: 'response',
        duration: error.config?.metadata 
          ? new Date() - error.config.metadata.startTime 
          : null,
      };

      // Don't track authentication errors as errors (expected behavior)
      if (error.response?.status === 401) {
        errorTracker.log(
          'Authentication required',
          'info',
          { url: error.config?.url }
        );
      } else if (isDemoRestriction) {
        toast.error(responseDetail, {
          id: 'demo-restriction',
          duration: 5000,
        });
      } else {
        errorTracker.captureAPIError(error, errorContext);
      }

      return Promise.reject(error);
    }
  );

  console.log('✅ Axios interceptors configured');
};

export default setupAxiosInterceptors;
